import {
  ShareInfoFragment,
  useVersionDeletedSubscription,
  ShareInfoFragmentDoc,
} from 'gqlTypes'
import { dataIdFromObject, useShortIdsMapper } from 'graphql/cache'
import { deleteEntityFromCache, useEventDispatch } from 'utils'

import {
  shouldReduceTotalCount,
  wasDeletionInitiatedByThisClient,
} from './deletedVersionsTracking'
import { updateVersionHistory } from './updateVersionHistory'
import { ErrorHandler } from 'modules/tracing'

declare module 'utils' {
  export interface EventsMap {
    versionDeleted: {
      /**
       * shortId of previous latest version
       */
      versionShortId: string
      share: ShareInfoFragment
    }
  }
}

export const useVersionDeleted = () => {
  const onVersionDeleted = useEventDispatch('versionDeleted')
  const shortIdsMapper = useShortIdsMapper()

  useVersionDeletedSubscription({
    onSubscriptionData: ({
      client,
      subscriptionData: { loading, data, error },
    }) => {
      if (!data || error || loading) {
        ErrorHandler.shouldNeverHappen(
          'versionDeleted event should contain valid data'
        )
        return
      }

      const { share, versionDeleted } = data.versionDeleted

      updateVersionHistory({
        client,
        variables: { shareIdentifier: share.identifier },
        update: share => {
          if (shouldReduceTotalCount(versionDeleted.identifier)) {
            share.availableVersions.meta.totalCount--
          }
          const index = share.versionHistory.entries.findIndex(
            x => x.identifier === versionDeleted.identifier
          )
          share.versionHistory.entries[index] = versionDeleted
        },
      })

      const versionShortId = shortIdsMapper.toShortId('Version', versionDeleted)
      if (versionShortId) {
        if (!wasDeletionInitiatedByThisClient(versionDeleted.identifier)) {
          onVersionDeleted({ versionShortId, share })
        }
      }

      client.writeFragment<ShareInfoFragment>({
        data: share,
        fragment: ShareInfoFragmentDoc,
        id: dataIdFromObject(share)!,
        fragmentName: 'ShareInfo',
      })

      if (versionShortId) {
        // we have to remove data from the cache only when we known that we'll not need to access older data
        deleteEntityFromCache(client.cache, {
          __typename: 'Version',
          shortId: versionShortId,
        })
      }
    },
    fetchPolicy: 'no-cache',
  })
}
